import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const UniversesSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const universes = [
    {
      title: 'VYBE ÉVENT',
      subtitle: 'ÉVÉNEMENTS D\'EXCEPTION',
      description: 'Soirées privées sur péniches, rooftops parisiens, showcases exclusifs. Organisation clé en main d\'événements corporate, mariages VIP et célébrations inoubliables. Chaque moment devient une expérience unique.',
      image: 'https://public.readdy.ai/ai/img_res/c58288ca47990c36ae6a87ca5d14a994.jpg',
      link: '/events',
      buttonText: 'Découvrir VYBE ÉVENT',
      badge: '🥂 CÉLÉBRATION',
      color: 'from-purple-500 via-pink-500 to-rose-500',
      iconClass: 'ri-goblet-fill'
    },
    {
      title: 'VYBE BY QUAD',
      subtitle: 'AVENTURE URBAINE & NATURE',
      description: 'Balades en quad électrique dans Paris (1h30-2h) entre monuments iconiques. Randonnées nature dans le Vexin et forêts environnantes. Expériences uniques avec photos GoPro et drone incluses.',
      image: 'https://readdy.ai/api/search-image?query=electric%20quad%20bike%20adventure%20in%20Paris%20streets%2C%20modern%20ATV%20urban%20exploration%20near%20Eiffel%20Tower%2C%20exciting%20city%20tour%20on%20electric%20quad%2C%20premium%20outdoor%20adventure%20activity%2C%20dynamic%20action%20photography%20of%20quad%20riding%2C%20urban%20landscape%20exploration%2C%20thrilling%20Paris%20experience%2C%20professional%20adventure%20photography%2C%20stylish%20electric%20vehicle%20tour&width=800&height=600&seq=vybe-quad-adventure-001&orientation=landscape',
      link: '/quadlux',
      buttonText: 'Vivre l\'Aventure',
      badge: '⚡ ADRÉNALINE',
      color: 'from-orange-500 via-amber-500 to-yellow-500',
      iconClass: 'ri-riding-fill'
    },
    {
      title: 'VYBE AGENCY',
      subtitle: 'CRÉATION DIGITALE',
      description: 'Agence créative spécialisée en stratégie digitale, social media management, création de contenu viral et marketing d\'influence. Transformez votre présence en ligne avec notre expertise.',
      image: 'https://readdy.ai/api/search-image?query=modern%20digital%20creative%20agency%20workspace%2C%20social%20media%20content%20creation%20studio%2C%20professional%20photography%20and%20videography%20setup%2C%20influencer%20marketing%20team%20at%20work%2C%20contemporary%20creative%20office%20with%20holographic%20displays%2C%20digital%20strategy%20planning%2C%20content%20creator%20workspace%2C%20premium%20agency%20environment%2C%20innovative%20digital%20marketing&width=800&height=600&seq=vybe-agency-digital-001&orientation=landscape',
      link: '/vybe',
      buttonText: 'Créer avec VYBE',
      badge: '🎨 CRÉATION',
      color: 'from-blue-500 via-cyan-500 to-teal-500',
      iconClass: 'ri-palette-fill'
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.universe-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 px-4 bg-gradient-to-b from-[#0A0A0A] via-[#0F0F0F] to-[#0A0A0A] overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-[#C0C0C0]/10 to-[#FFD700]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="text-[#FFD700] text-sm font-bold tracking-widest uppercase">Nos Univers</span>
            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#FFD700] to-transparent mt-2"></div>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Trois <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Pôles</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Une vision créative, trois expertises complémentaires pour créer des moments exceptionnels
          </p>
        </div>

        {/* Universe Cards with 3D Effects */}
        <div className="space-y-32">
          {universes.map((universe, index) => (
            <div
              key={universe.title}
              className={`universe-card relative opacity-0 ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
              style={{ animationDelay: `${index * 0.2}s` }}
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className={`flex flex-col lg:flex-row items-center gap-12 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}>
                {/* Image with 3D Hover Effect */}
                <div className="flex-1 w-full">
                  <div className="relative group perspective-1000">
                    <div className={`relative overflow-hidden rounded-3xl transition-all duration-700 ${
                      activeCard === index ? 'scale-105 rotate-y-5' : ''
                    }`}>
                      {/* Glow effect */}
                      <div className={`absolute -inset-1 bg-gradient-to-r ${universe.color} opacity-0 group-hover:opacity-75 blur-2xl transition-opacity duration-700 rounded-3xl`}></div>
                      
                      {/* Image */}
                      <div className="relative">
                        <img
                          src={universe.image}
                          alt={universe.title}
                          className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent"></div>
                        
                        {/* Glassmorphism Badge */}
                        <div className={`absolute top-8 left-8 px-6 py-3 bg-gradient-to-r ${universe.color} backdrop-blur-xl rounded-full border border-white/20 shadow-2xl`}>
                          <span className="text-white font-bold text-sm flex items-center gap-2">
                            <span>{universe.badge}</span>
                          </span>
                        </div>
                        
                        {/* Number Badge */}
                        <div className="absolute bottom-8 right-8">
                          <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${universe.color} flex items-center justify-center shadow-2xl transform group-hover:rotate-180 transition-transform duration-700`}>
                            <span className="text-white font-black text-3xl">{index + 1}</span>
                          </div>
                        </div>

                        {/* Icon */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <i className={`${universe.iconClass} text-9xl text-white/20`}></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="mb-6">
                    <h3 
                      className="text-5xl md:text-6xl font-black text-white mb-4 hover:scale-105 transition-transform inline-block"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      <span className={`bg-gradient-to-r ${universe.color} bg-clip-text text-transparent`}>
                        {universe.title}
                      </span>
                    </h3>
                    
                    <div className={`inline-block px-6 py-2 bg-gradient-to-r ${universe.color} rounded-full`}>
                      <span className="text-white font-bold text-sm tracking-wide">
                        {universe.subtitle}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-white/80 text-lg md:text-xl mb-10 leading-relaxed max-w-xl">
                    {universe.description}
                  </p>
                  
                  <Link
                    to={universe.link}
                    className={`group inline-flex items-center gap-4 px-10 py-5 bg-gradient-to-r ${universe.color} text-white font-bold rounded-2xl hover:scale-105 transition-all duration-500 shadow-2xl hover:shadow-3xl relative overflow-hidden`}
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      <i className={`${universe.iconClass} text-2xl`}></i>
                      {universe.buttonText}
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform"></i>
                    </span>
                    <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vision Statement with Glassmorphism */}
        <div className="mt-32 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 blur-3xl"></div>
          
          <div className="relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-2xl rounded-3xl p-12 md:p-16 border border-white/20 shadow-2xl">
            <div className="text-center">
              <i className="ri-sparkling-2-fill text-6xl text-[#FFD700] mb-6 inline-block animate-pulse"></i>
              
              <h3 className="text-4xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Une Vision, <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Trois Expertises</span>
              </h3>
              
              <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto mb-10 leading-relaxed">
                VYBE PARIS réunit création digitale, aventures urbaines et événementiel d'exception 
                dans un écosystème unique dédié aux expériences premium.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { icon: 'ri-lightbulb-flash-fill', text: 'Innovation', color: 'from-purple-500 to-pink-500' },
                  { icon: 'ri-heart-3-fill', text: 'Passion', color: 'from-pink-500 to-rose-500' },
                  { icon: 'ri-star-fill', text: 'Excellence', color: 'from-orange-500 to-yellow-500' },
                  { icon: 'ri-team-fill', text: 'Collaboration', color: 'from-blue-500 to-cyan-500' }
                ].map((value, idx) => (
                  <div
                    key={value.text}
                    className={`group px-8 py-4 bg-gradient-to-r ${value.color} rounded-full border border-white/20 hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-2xl`}
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <span className="text-white font-semibold flex items-center gap-3">
                      <i className={`${value.icon} text-xl group-hover:rotate-12 transition-transform`}></i>
                      {value.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
        
        .rotate-y-5 {
          transform: rotateY(5deg);
        }
      `}</style>
    </section>
  );
};

export default UniversesSection;
